import { useEffect, useMemo, useState } from "react";

import Tickbox from "./tickbox";

const days = [
  { key: "1", label: "Mon" },
  { key: "2", label: "Tue" },
  { key: "3", label: "Wed" },
  { key: "4", label: "Thu" },
  { key: "5", label: "Fri" },
  { key: "6", label: "Sat" },
  { key: "7", label: "Sun" },
];

const emptyDay = {
  startTime: "",
  breaksQty: null,
  finishTime: "",
  dayTotalHours: null,
};

const isValidTime = (value) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(value || "");

const minutesFromTime = (value) => {
  if (!isValidTime(value)) return null;
  const [hours, minutes] = value.split(":").map((part) => parseInt(part, 10));
  return hours * 60 + minutes;
};

const calculateDayTotalHours = (
  startTime,
  finishTime,
  breaksQty,
  breaksPaid,
) => {
  const startMinutes = minutesFromTime(startTime);
  const finishMinutes = minutesFromTime(finishTime);
  if (startMinutes === null || finishMinutes === null) return null;
  if (finishMinutes <= startMinutes) return null;
  const breakHours =
    !breaksPaid && typeof breaksQty === "number" ? breaksQty : 0;
  const hours = Math.max((finishMinutes - startMinutes) / 60 - breakHours, 0);
  return parseFloat(hours.toFixed(2));
};

const normaliseMatrix = (value, breaksPaid) => {
  const source = value && typeof value === "object" ? value : {};
  const matrix = {};
  days.forEach((day) => {
    const dayValue = source[day.key] || {};
    const startTime = dayValue.startTime || "";
    const finishTime = dayValue.finishTime || "";
    const breaksQty =
      dayValue.breaksQty === 0
        ? 0
        : dayValue.breaksQty
          ? parseFloat(dayValue.breaksQty)
          : null;
    matrix[day.key] = {
      startTime,
      breaksQty,
      finishTime,
      dayTotalHours: calculateDayTotalHours(
        startTime,
        finishTime,
        breaksQty,
        breaksPaid,
      ),
    };
  });
  return matrix;
};

const WorkPatternSpecificsMatrix = ({
  name,
  value,
  setValue,
  disabled,
  onKeyDown,
  breaksPaidName,
  breaksPaidValue,
}) => {
  const [breaksPaid, setBreaksPaid] = useState(!!breaksPaidValue);
  const initialMatrix = useMemo(
    () => normaliseMatrix(value, breaksPaid),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  );
  const [matrix, setMatrix] = useState(initialMatrix);

  const weeklyTotalHours = useMemo(() => {
    return Object.values(matrix).reduce((total, day) => {
      const hours =
        typeof day?.dayTotalHours === "number" ? day.dayTotalHours : 0;
      return total + hours;
    }, 0);
  }, [matrix]);

  useEffect(() => {
    setMatrix(initialMatrix);
    setValue(name, initialMatrix);
  }, [initialMatrix, name, setValue]);

  useEffect(() => {
    setBreaksPaid(!!breaksPaidValue);
  }, [breaksPaidValue]);

  useEffect(() => {
    if (breaksPaidName) setValue(breaksPaidName, breaksPaid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breaksPaid, breaksPaidName]);

  const updateDay = (dayKey, change) => {
    const current = matrix[dayKey] || emptyDay;
    const nextDay = {
      ...current,
      ...change,
    };
    nextDay.dayTotalHours = calculateDayTotalHours(
      nextDay.startTime,
      nextDay.finishTime,
      nextDay.breaksQty,
      breaksPaid,
    );

    const nextMatrix = {
      ...matrix,
      [dayKey]: nextDay,
    };

    setMatrix(nextMatrix);
    setValue(name, nextMatrix);
  };

  const onBreaksPaidChange = (checked) => {
    setBreaksPaid(checked);
    const nextMatrix = {};
    Object.keys(matrix).forEach((dayKey) => {
      const current = matrix[dayKey] || emptyDay;
      nextMatrix[dayKey] = {
        ...current,
        dayTotalHours: calculateDayTotalHours(
          current.startTime,
          current.finishTime,
          current.breaksQty,
          checked,
        ),
      };
    });
    setMatrix(nextMatrix);
    setValue(name, nextMatrix);
  };

  return (
    <div>
      <p className="work-pattern-matrix-info" role="note">
        Changes to these work pattern details may affect holiday calculations.
      </p>
      <div className="work-pattern-matrix-breaks-paid">
        <Tickbox
          id={`${name}-breaks-paid`}
          name={breaksPaidName || `${name}-breaks-paid`}
          checked={breaksPaid}
          disabled={disabled}
          trigger={breaksPaid}
          onKeyDown={onKeyDown}
          onChange={(checked) => onBreaksPaidChange(checked)}
        />
        <label htmlFor={`${name}-breaks-paid`}>
          Breaks paid (not deducted from total hours)
        </label>
      </div>
      <div className="work-pattern-matrix">
        <table>
          <thead>
            <tr>
              <th>Day</th>
              <th>Start</th>
              <th>Breaks (hours)</th>
              <th>Finish</th>
              <th>Total hours</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => {
              const row = matrix[day.key] || emptyDay;
              return (
                <tr key={day.key}>
                  <td>{day.label}</td>
                  <td>
                    <input
                      type="time"
                      value={row.startTime || ""}
                      disabled={disabled}
                      onKeyDown={onKeyDown}
                      onChange={(e) => {
                        updateDay(day.key, { startTime: e.target.value || "" });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      value={row.breaksQty ?? ""}
                      disabled={disabled}
                      onKeyDown={onKeyDown}
                      onChange={(e) => {
                        const parsed = parseFloat(e.target.value);
                        updateDay(day.key, {
                          breaksQty: Number.isNaN(parsed) ? null : parsed,
                        });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={row.finishTime || ""}
                      disabled={disabled}
                      onKeyDown={onKeyDown}
                      onChange={(e) => {
                        updateDay(day.key, {
                          finishTime: e.target.value || "",
                        });
                      }}
                    />
                  </td>
                  <td>
                    <span>{row.dayTotalHours ?? "-"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="work-pattern-matrix-total">
        Total weekly hours: {weeklyTotalHours.toFixed(2)}
      </p>
    </div>
  );
};

export default WorkPatternSpecificsMatrix;
